import { occFetch, occUrl } from "~/app/api/occ/occFetch";
import dayjs from "dayjs";
import type { FilterNames } from "~/features/productDetails/components/MMDAssistant/MMDDialog";
import type { MoxyMapDTO } from "~/common/types/MoxyMapDTO";

export interface MmdFilterOptionWsDTO {
  value: string;
  disabled: boolean;
}

type DropdownEntryData = MmdFilterOptionWsDTO;

export interface MMDAssistantRequestData {
  marketDesignation: string;
  designations: string[];
  packaging: string[];
  customerMaterialNumbers: string[];
  quantity: number;
  rdd: Date | null;
  priceReference: string;
  filterOrder: FilterNames[];
}

export interface MMDAssistantResponseDTO {
  results: MMDAssistantCalculationResultDTO[];
  filterOptions: {
    products: DropdownEntryData[];
    packaging: DropdownEntryData[];
    customerMaterialNumbers: DropdownEntryData[];
  };
}

export interface MMDAssistantResponseItemDTO {
  estimatedShippingDate: string; // "YYYY-MM-DD"
  productCode: string;
  productDesignation: string;
  sapMaterialNumber: string;
  packCode: string;
  packSize: number;
  customerMaterialNumber: string[];
  quantity: number;
}

interface MMDAssistantResponseItemWsDTO extends Omit<MMDAssistantResponseItemDTO, "customerMaterialNumber"> {
  customerMaterialNumber?: string[];
}

interface MMDAssistantRequestApiDTO {
  marketDesignation: string;
  quantity: number;
  rdd: string | null;
  filters: {
    field: string;
    values: string[];
  }[];
}

export interface MMDAssistantCalculationResultDTO {
  title: string;
  tag?: string;
  data: MMDAssistantResponseItemDTO[];
}

interface MMDAssistantCalculationResultWsDTO extends Omit<MMDAssistantCalculationResultDTO, "data"> {
  data?: MMDAssistantResponseItemWsDTO[];
}

interface NormalizedMMDAssistantResponse {
  results: MMDAssistantCalculationResultDTO[];
  filters: Record<string, MmdFilterConfigWsDTO>;
  error?: string;
}

export interface MmdFilterConfigWsDTO {
  field: string;
  values?: MmdFilterOptionWsDTO[];
}

/** Raw response shape emitted by MOXy for MMDSimulatorResultsWsDTO. */
export interface MMDSimulatorResultsWsDTO {
  results?: MMDAssistantCalculationResultWsDTO[];
  filters?: MoxyMapDTO<string, MmdFilterConfigWsDTO>;
  error?: string;
}

function normalizeMmdSimulatorResults(response: MMDSimulatorResultsWsDTO): NormalizedMMDAssistantResponse {
  return {
    ...response,
    results: (response.results ?? []).map((result) => ({
      ...result,
      data: (result.data ?? []).map((item) => ({
        ...item,
        customerMaterialNumber: item.customerMaterialNumber ?? [],
      })),
    })),
    filters: Object.fromEntries((response.filters ?? []).map(({ key, value }) => [key, value])),
  };
}

/**
 * Fetches MMD Assistant data from the server
 * @param data The form data to send to the server
 * @returns Promise with the MMD table data
 */
export const fetchMMDAssistantData = async (data: MMDAssistantRequestData): Promise<MMDAssistantResponseDTO> => {
  const url = occUrl("/api/mmd/availabilitySimulator");

  const apiData: MMDAssistantRequestApiDTO = {
    marketDesignation: data.marketDesignation,
    quantity: data.quantity,
    rdd: data.rdd ? dayjs(data.rdd).format("YYYY-MM-DD") : null,
    filters: data.filterOrder.map((filterName) => ({
      field: filterName,
      values: data[filterName],
    })),
  };

  try {
    const response = await occFetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(apiData),
    });

    if (!response.ok) {
      throw new Error(`MMD Assistant API error: ${response.status}`);
    }

    const wireData = (await response.json()) as MMDSimulatorResultsWsDTO;
    const resultData = normalizeMmdSimulatorResults(wireData);
    const filterOptionsMap = resultData.filters;
    return {
      results: resultData.results,
      filterOptions: {
        products: filterOptionsMap["designations"]?.values ?? [],
        packaging: filterOptionsMap["packaging"]?.values ?? [],
        customerMaterialNumbers: filterOptionsMap["customerMaterialNumbers"]?.values ?? [],
      },
    };
  } catch (error) {
    console.error("Error fetching MMD Assistant data:", error);
    throw error;
  }
};
